import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { BiTrash, BiBookHeart } from 'react-icons/bi'
import { getMannersList, deleteManners } from '../../actions/directoryBasicEducation'
import Button from '../../components/Button'
import Table from '../../components/Table'
import Confirmation from '../../components/Confirmation/Confirmation'
import MannersModal from './Modal'
import styles from './styles.module.scss'

const TABLE_HEADERS = [
  { key: 'name', label: 'Название' },
  { key: 'actions', label: '', width: '60px' }
]

export default function Manners() {
  const dispatch = useDispatch()
  const { mannersList, loading } = useSelector(state => state.directoryBasicEducation)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortAsc, setSortAsc] = useState(true)

  useEffect(() => {
    dispatch(getMannersList())
  }, [dispatch])

  const handleDelete = useCallback((item) => (e) => {
    e.stopPropagation()
    setConfirmation({ id: item.id, name: item.name })
  }, [])

  const openModal = useCallback((item) => () => {
    setSelectedItem(item || {})
  }, [])

  const closeModal = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const acceptConfirmation = useCallback(async () => {
    if (!confirmation) return
    setIsDeleting(true)
    try {
      await dispatch(deleteManners(confirmation.id))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const sortedList = useMemo(() => {
    if (!mannersList) return []
    return [...mannersList].sort((a, b) => sortAsc
      ? a.name?.localeCompare(b.name, 'ru')
      : b.name?.localeCompare(a.name, 'ru'))
  }, [mannersList, sortAsc])

  const isModalOpen = selectedItem !== null
  const isAddMode = selectedItem && !selectedItem.id

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <BiBookHeart size={64} className={styles.emptyIcon} />
      <p>Нет добавленных критериев воспитанности</p>
      <Button onClick={openModal()}>Добавить первый критерий</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка критериев воспитанности...</span>
    </div>
  )

  const renderTable = () => (
    <div className={styles.tableContainer}>
      <Table className={styles.table}>
        <thead>
          <tr>
            {TABLE_HEADERS.map(({ key, label, width }) => (
              <th
                key={key}
                style={{ ...(width ? { width } : {}), ...(key === 'name' ? { cursor: 'pointer' } : {}) }}
                onClick={key === 'name' ? () => setSortAsc(v => !v) : undefined}
              >{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedList.map((item) => (
            <tr
              key={item.id}
              onClick={openModal(item)}
              className={styles.tableRow}
            >
              <td>
                <span className={styles.itemName}>{item.name}</span>
              </td>
              <td>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleDelete(item)}
                  title={`Удалить критерий ${item.name}`}
                  aria-label={`Удалить критерий ${item.name}`}
                >
                  <BiTrash size={20} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className={styles.actions}>
        <Button onClick={openModal()}>Добавить</Button>
        <span className={styles.counter}>
          Всего: {sortedList.length}
        </span>
      </div>
    </div>
  )

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1>Критерии воспитанности</h1>
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderTable()}
      </main>

      <MannersModal
        open={isModalOpen}
        onClose={closeModal}
        data={selectedItem}
        addMode={isAddMode}
      />

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить "${confirmation.name}"?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}
